import type {
  DataSourceDeclaration,
  HttpDataSourceDeclaration,
  InferenceDataSourceDeclaration,
  ReportingModelDataSourceDeclaration,
} from '@de/providers/DataSourceManager.types';
import { readDeclaredKind } from './source-type-registry';
import { resolveDataSourceType } from './source-types';

export type DataSourceKind = 'http' | 'reportingModel' | 'inference';

export function getDataSourceKind(
  decl: DataSourceDeclaration,
): DataSourceKind | string {
  return readDeclaredKind(decl);
}

export function isHttpDataSourceDeclaration(
  decl: DataSourceDeclaration,
): decl is HttpDataSourceDeclaration {
  return decl.kind === undefined || decl.kind === 'http';
}

export function isReportingModelDataSourceDeclaration(
  decl: DataSourceDeclaration,
): decl is ReportingModelDataSourceDeclaration {
  return decl.kind === 'reportingModel';
}

export function isInferenceDataSourceDeclaration(
  decl: DataSourceDeclaration,
): decl is InferenceDataSourceDeclaration {
  return decl.kind === 'inference';
}

/** Whether a source type is registered for this declaration's `kind`. */
export function isKnownDataSourceKind(decl: DataSourceDeclaration): boolean {
  return resolveDataSourceType(decl) !== null;
}

/**
 * Whether a declaration carries enough to be registered with the manager.
 *
 * This is the normative rule set for what a `dataSources` entry must contain,
 * and it is enforced here at the point declarations enter the runtime. Note
 * what happens to a declaration that fails: it is **dropped**, silently, with
 * no error raised anywhere, so every component bound to it sits at `pending`
 * forever. That silence is why `dynamic-flows` mirrors this predicate as a
 * publish-time schema — the failure has to surface while someone is still
 * looking at the document.
 *
 * The two must agree. `dynamic-flows` has a table asserting its schema's
 * verdict against this predicate's for each probe row, and this module has the
 * matching half, so a change on either side turns one of the two red.
 */
export function isSupportedDataSourceDeclaration(
  decl: DataSourceDeclaration,
): boolean {
  return resolveDataSourceType(decl)?.isSupported(decl) ?? false;
}

/**
 * Values that may contain {{…}} templates and gate fetch / formState re-runs.
 *
 * An unregistered kind reports no interpolatable fields, which is the same
 * answer it gives everywhere else: nothing here knows what such a declaration
 * means, so it does not get to claim a field is a template.
 */
export function getDeclarationInterpolatableFields(
  decl: DataSourceDeclaration,
): readonly unknown[] {
  return resolveDataSourceType(decl)?.interpolatableFields(decl) ?? [];
}
